import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Sparkles, Brain, Stars, Moon } from 'lucide-react';

export const HomePage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Brain className="w-8 h-8" />,
      title: 'Analisis Neural',
      description: 'Pemetaan pola neurologis berdasarkan data kelahiran Anda'
    },
    {
      icon: <Stars className="w-8 h-8" />,
      title: 'Ramalan Kosmik',
      description: 'Interpretasi celestial yang mendalam dan personal'
    },
    {
      icon: <Moon className="w-8 h-8" />,
      title: 'Kebijaksanaan Kuno',
      description: 'Menggabungkan pengetahuan tradisional dengan teknologi modern'
    }
  ];

  return (
    <div className="min-h-screen cosmic-bg">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Hero Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1597733336794-12d05021d510" 
            alt="Cosmic background"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-4 py-20 md:py-32">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            {/* Floating Icon */}
            <div className="inline-flex items-center justify-center mb-6">
              <div className="relative animate-float">
                <div className="absolute inset-0 blur-xl opacity-50 bg-primary rounded-full"></div>
                <Sparkles className="relative w-16 h-16 text-primary" />
              </div>
            </div>

            {/* Main Heading */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
              <span className="gradient-text">Ramalan Neurology</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Jelajahi rahasia kosmik yang tersembunyi dalam pola neurologis Anda. 
              Temukan takdir melalui perpaduan sains dan mistisisme.
            </p>

            {/* CTA Button */}
            <div className="pt-4">
              <Button 
                size="lg"
                onClick={() => navigate('/input')}
                className="text-lg px-8 py-6 bg-primary hover:bg-primary/90 shadow-cosmic hover:shadow-glow transition-all duration-300 hover:scale-105"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Mulai Ramalan Anda
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="relative z-10 container mx-auto px-4 py-16 md:py-24">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Ketahui <span className="text-primary">Tentang Diri Kamu</span>
            </h2>
            <p className="text-muted-foreground text-lg">
              Pengalaman ramalan yang unik dan personal untuk Anda
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {features.map((feature, index) => (
              <Card 
                key={index}
                className="glass-card p-8 hover-glow group cursor-pointer transition-all duration-300 hover:-translate-y-2"
              >
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="p-4 rounded-full bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300 animate-pulse-glow">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Mystical Divider */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center space-x-4">
          <div className="h-px w-24 bg-gradient-to-r from-transparent via-primary to-transparent"></div>
          <Stars className="w-6 h-6 text-primary animate-pulse" />
          <div className="h-px w-24 bg-gradient-to-r from-transparent via-primary to-transparent"></div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="relative z-10 container mx-auto px-4 py-16 md:py-24">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Cara <span className="text-accent">Kerja</span>
            </h2>
            <p className="text-muted-foreground text-lg">
              Proses sederhana untuk mendapatkan ramalan Anda
            </p>
          </div>

          <div className="space-y-6">
            {[
              { step: '01', title: 'Masukkan Data', desc: 'Berikan tanggal lahir dan nama lengkap Anda' },
              { step: '02', title: 'Analisis Kosmik', desc: 'Sistem kami menganalisis pola neurologis dan celestial' },
              { step: '03', title: 'Terima Ramalan', desc: 'Dapatkan ramalan personal yang mendalam dan akurat' }
            ].map((item, index) => (
              <div key={index} className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-primary/10 border-2 border-primary flex items-center justify-center">
                    <span className="text-2xl font-bold text-primary">{item.step}</span>
                  </div>
                </div>
                <div className="flex-1 glass-card p-6 rounded-lg">
                  <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="relative z-10 container mx-auto px-4 py-16 md:py-24">
        <Card className="glass-card p-12 text-center max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Siap Menemukan <span className="gradient-text">Takdir Anda?</span>
          </h2>
          <p className="text-muted-foreground text-lg mb-8">
            Mulai perjalanan spiritual Anda sekarang dan temukan rahasia yang tersembunyi dalam bintang-bintang
          </p>
          <Button 
            size="lg"
            onClick={() => navigate('/input')}
            className="text-lg px-8 py-6 bg-accent hover:bg-accent/90 shadow-glow transition-all duration-300 hover:scale-105"
          >
            Mulai Sekarang
          </Button>
        </Card>
      </div>

      {/* Footer */}
      <footer className="relative z-10 border-t border-border/50 mt-20">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center text-muted-foreground">
            <p>© 2024 Numerology. Created By @m_iksan273.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;